// frontend/src/core/branding/BrandingContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TenantBranding } from '../types/tenant.types';
import { tenantResolver } from '../tenant/TenantResolver';
import { BrandingLoader } from './BrandingLoader';

interface BrandingContextValue {
  branding: TenantBranding | null;
  isLoading: boolean;
  error: string | null;
  projectName: string;
}

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string>('');

  useEffect(() => {
    const loadBranding = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // FORCE TENANT RE-RESOLUTION (in case ?app= param changed)
        tenantResolver['currentTenant'] = null; // Reset cache
        const tenant = tenantResolver.resolveTenant();
        setProjectName(tenant);

        // LOAD BRANDING
        const brandingData = await BrandingLoader.loadBranding(tenant);
        setBranding(brandingData);

        // APPLY BRANDING TO DOCUMENT
        BrandingLoader.applyBranding(brandingData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load branding';
        setError(errorMessage);
        console.error('Branding loading error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadBranding();
  }, [location.search]); // Re-run when URL query changes!

  return (
    <BrandingContext.Provider value={{ branding, isLoading, error, projectName }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = (): BrandingContextValue => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};